/**
 * Client-management domain types — self-contained copy for the admin SPA
 * (ported from apps/auction/src/features/clients/types/*). IDs and dates are
 * strings here (api-node serialises Mongo ObjectId / Date to JSON strings);
 * the web feature typed some as ObjectId/Date for its server-side consumers.
 */
import type {
  BiddingJournalEntry,
  BuyingPower,
  ClientBidding,
  ClientSeesLabel,
  UserRoleJournalEntry,
} from '@carsfinders/contracts';

export enum VerificationStatus {
  PENDING = 'pending',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

export enum VerificationLevel {
  BASIC = 'basic',
  STANDARD = 'standard',
  PREMIUM = 'premium',
}

export enum DocumentVerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

export interface DiaVerificationData {
  verified: boolean;
  verificationDate: string;
  documentType: string;
  documentNumber: string;
  fullName: string;
  birthDate: string;
  citizenship: string;
}

export interface UserDocument {
  _id?: string;
  userId: string;
  documentType: string;
  documentNumber: string;
  taxID: string;
  citizenship: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  fileName?: string;
  fileSize?: number;
  fileUrl?: string;
  uploadedAt?: string;
  verificationStatus?: DocumentVerificationStatus;
  verifiedAt?: string;
  verifiedBy?: string;
  rejectionReason?: string;
  ocrConfidence?: number;
  documentHash?: string;
  expiryWarningsSent?: number;
}

export interface User {
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  displayName: string;
  phone?: string;
  role?: string[];
  photoURL?: string;
  createdAt?: string;
  password?: string;
  verified?: boolean;
  documents?: UserDocument[];
  verificationStatus?: VerificationStatus;
  verificationLevel?: VerificationLevel;
  kycCompletedAt?: string;
  lastDocumentUpload?: string;
  riskScore?: number;
  diaVerified?: boolean;
  diaVerificationData?: DiaVerificationData;
  onboardingCompleted?: boolean;
  onboardingStep?: number;
  bidLimit?: number;
  /**
   * Phase 14 (SC-16) ladder facts. `GET /users` projects everything but the
   * password, so these arrive as ISO strings and the numeric rung 0-4 is
   * derived from them with `deriveVerificationLevel` — never read off
   * `verificationLevel`, which is the LEGACY STRING above and a different
   * concept entirely.
   */
  emailVerified?: string | null;
  phoneVerifiedAt?: string | null;
  identityVerifiedAt?: string | null;
  identityProvider?: 'sumsub' | 'diia' | 'manual' | null;
  /**
   * The latest «Запросити документ» (KYC defects, 2026-09-30). Whether it is
   * still OPEN is derived with `isKycDocumentRequestOpen` (contracts).
   */
  kycDocumentRequest?: {
    requestedAt: string;
    requestedBy: string;
    note: string | null;
  } | null;
  /** Phase 10 solvency fields (contracts/user.ts). `biddingPower` IS the deposit in CHF. */
  biddingPower?: number | null;
  exposureLimitChf?: number | null;
  exposureExempt?: boolean | null;
  plan?: 'retail' | 'dealer' | 'dealer_pro' | null;
  /**
   * Volume tiers (contracts/volume-tier.ts): end of the running tier hold and
   * the last evaluation, ISO strings over the wire (`GET /users` projects them).
   */
  planHoldUntil?: string | null;
  planEvaluatedAt?: string | null;
}

/** `users.bidding` as it arrives over the wire — `changedAt` is an ISO string, not a Date. */
export type WireClientBidding = Omit<ClientBidding, 'changedAt'> & { changedAt: string };

/**
 * GET /admin/users/:id/bidding-mode `data` (apps/api BiddingModeSnapshot).
 * `buyingPower` / `clientSees` are null for a legacy client (no mode yet).
 */
export interface BiddingModeSnapshot {
  bidding: WireClientBidding | null;
  legacy: boolean;
  depositChf: number;
  committedChf: number;
  activeBidCount: number;
  buyingPower: BuyingPower | null;
  clientSees: ClientSeesLabel | null;
  refundRequestedAt: string | null;
}

/**
 * One GET /admin/users/:id/bidding-mode/journal row, newest first — the
 * contracts `BiddingJournalEntry` with its dates as ISO strings. Carries the
 * optional `depositKeptAsPrepaymentChf` (owner decision d, 2026-09-28): CHF a
 * client held when switched TO trusted, kept as a prepayment for the next invoice.
 */
export type WireBiddingJournalEntry = Omit<
  BiddingJournalEntry,
  'previous' | 'next' | 'changedAt'
> & {
  previous: WireClientBidding | null;
  next: WireClientBidding;
  changedAt: string;
};

/** PUT /admin/users/:id/bidding-mode `data` (apps/api BiddingModeChange). */
export interface BiddingModeChangeResult {
  previous: WireClientBidding | null;
  next: WireClientBidding;
  /** CHF kept as a prepayment by this change; 0 when nothing was kept. */
  depositKeptAsPrepaymentChf: number;
}

/**
 * One GET /admin/users/:id/roles/journal row, newest first — the contracts
 * `UserRoleJournalEntry` with its date as an ISO string.
 */
export type WireUserRoleJournalEntry = Omit<UserRoleJournalEntry, 'changedAt'> & {
  changedAt: string;
};

export enum PaymentMethod {
  Cash = 'Cash',
  BankTransfer = 'Bank Transfer',
  Card = 'Card',
  Other = 'Other',
}

/** A legacy `clientPayments` row — read-only in the tab's «Архів (стара система)». */
export interface ClientPayment {
  _id?: string;
  userId: string;
  amount: number;
  paymentDate: string;
  paymentMethod?: PaymentMethod;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export type ClientFilterType = 'all' | 'verified' | 'unverified' | 'activeBids';

export interface ClientFilters {
  filter: ClientFilterType;
  search?: string;
}

export type SortOption = '' | 'createdAt:desc' | 'createdAt:asc' | 'name:asc' | 'name:desc';

/** Bid status values — mirrors the web BidStatus enum (only the values the
 * clients list/filters read). */
export enum BidStatus {
  Active = 'Under Review',
  Outbid = 'Outbid',
  Cancelled = 'Not Won',
  Won = 'Won',
}

/**
 * A bid row as GET /bids returns it to an admin. The list screen only needs
 * owner, status and amount; the client-detail «Ставки» tab also prints the lot,
 * the date and the relay state, so those fields (all already in the same
 * response — nothing new is fetched) are typed here as optional. The car
 * fields come from the admin projection's `alpicar_cars` $lookup
 * (apps/api bid-read.helper.ts) and are null when the lot is gone.
 */
export interface ClientBid {
  _id?: string;
  userId: string;
  status: string;
  bid: number;
  carId?: string;
  createdAt?: string;
  relayStatus?: string;
  carTitle?: string | null;
  carThumbnailUrl?: string | null;
  carListingId?: string | null;
  /** The lot's real auction close (ISO, no customer offset); null when unknown. */
  carEndsAt?: string | null;
}

/** Minimal car display data used by the «Виграні» tab. */
export interface CarDisplayData {
  ai_brand?: string;
  ai_model?: string;
  brand?: string;
  model?: string;
  make?: string;
  listing_id?: string;
  thumbnail_image?: string;
  detail_images?: string[];
}

/** Minimal won-car shape — only the fields the «Виграні» tab reads. */
export interface WonCarWithDetails {
  _id?: string;
  userId?: string;
  bid?: { bid: number } | null;
  car?: CarDisplayData | null;
  auctionCommission?: number;
  delivery?: number;
  certificate?: number;
  terminal?: number;
  customsClearance?: number;
  repair?: number;
  sale?: number;
}
